import PlacementBadge from '@components/templates/placementBadge';
import ProductThumbnailPlaceholders from '@components/templates/productThumbnailPlaceholders';
import { TIMER_TYPE_OPTIONS } from '@lib/timerTypes';
import { Button } from '@nextui-org/react';
import { TimerType } from '@/types';

interface TimerTypeSelectionStepProps {
	onSelect: (timerType: TimerType) => void;
}

const ILLUSTRATION_TIMER_VALUES = [
	{ id: 'days', value: '0' },
	{ id: 'hours', value: '11' },
	{ id: 'minutes', value: '59' },
	{ id: 'seconds', value: '59' },
] as const;

const BAR_PRODUCT_PLACEHOLDERS = ['bar-product-1', 'bar-product-2', 'bar-product-3', 'bar-product-4'] as const;

function IllustrationTimerValues({ className }: { className: string }) {
	return (
		<div className={`flex items-center font-bold text-white ${className}`}>
			{ILLUSTRATION_TIMER_VALUES.map((item) => (
				<span key={item.id} className='rounded bg-[#29314E] px-1.5 py-0.5'>
					{item.value}
				</span>
			))}
		</div>
	);
}

function BarTypeIllustration() {
	return (
		<div className='h-full min-h-[260px] overflow-hidden bg-[#303647] p-6'>
			<div className='relative rounded-xl border-2 border-dashed border-[#747D9A] p-3'>
				<div className='flex h-9 items-center rounded bg-[#3E63F4] px-4'>
					<div className='h-3 w-20 rounded bg-white/35 sm:w-28' />
					<IllustrationTimerValues className='mx-auto gap-1.5 text-base' />
					<div className='h-4 w-20 rounded-full bg-white/35 sm:w-28' />
				</div>
				<PlacementBadge />
			</div>
			<div className='mt-6 h-4 w-52 max-w-[34%] rounded bg-[#46506A]' />
			<div className='mt-6 grid grid-cols-4 gap-4'>
				{BAR_PRODUCT_PLACEHOLDERS.map((id) => (
					<div key={id} className='h-[64px] rounded-lg bg-[#47516A]' />
				))}
			</div>
			<div className='mt-4 h-[64px] rounded-xl bg-[#47516A]' />
		</div>
	);
}

function ProductContainerTypeIllustration() {
	return (
		<div className='grid h-full min-h-[260px] grid-cols-1 gap-4 overflow-hidden bg-[#303647] p-6 sm:grid-cols-[1.05fr_0.95fr] sm:gap-6'>
			<div className='flex min-w-0 flex-col gap-3 sm:gap-4'>
				<div className='h-[92px] rounded-xl bg-[#46506A] sm:h-[150px]' />
				<ProductThumbnailPlaceholders className='h-9 sm:h-[48px]' />
			</div>
			<div className='flex min-w-0 flex-col gap-3 pt-1'>
				<div className='h-8 w-36 max-w-full rounded bg-[#46506A]' />
				<div className='h-4 rounded bg-[#46506A]' />
				<div className='relative rounded-xl border-2 border-dashed border-[#747D9A] p-2 sm:p-3'>
					<div className='rounded bg-[#3E63F4] px-4 py-3'>
						<div className='h-3 w-20 rounded bg-white/35' />
						<div className='mt-3 h-3 w-32 rounded bg-white/35' />
						<IllustrationTimerValues className='mt-4 justify-center gap-1.5 text-sm sm:text-base' />
					</div>
					<PlacementBadge />
				</div>
				<div className='mt-auto grid grid-cols-2 gap-4'>
					<div className='h-10 rounded bg-[#46506A]' />
					<div className='h-10 rounded bg-[#46506A]' />
				</div>
			</div>
		</div>
	);
}

export default function TimerTypeSelectionStep({ onSelect }: TimerTypeSelectionStepProps) {
	return (
		<div className='grid w-full grid-cols-1 gap-6 lg:grid-cols-2'>
			{TIMER_TYPE_OPTIONS.map((option) => (
				<article
					key={option.type}
					className='flex flex-col overflow-hidden rounded-2xl border border-default-200 bg-content1 shadow-sm'
				>
					{option.type === TimerType.BAR ? <BarTypeIllustration /> : <ProductContainerTypeIllustration />}
					<div className='flex flex-col gap-4 p-5 sm:flex-row sm:items-end sm:justify-between'>
						<div className='min-w-0'>
							<h3 className='text-xl font-bold leading-7'>{option.title}</h3>
							<p className='mt-2 text-base leading-6 text-default-500'>{option.description}</p>
						</div>
						<Button color='primary' className='shrink-0 px-7' onPress={() => onSelect(option.type)}>
							Select
						</Button>
					</div>
				</article>
			))}
		</div>
	);
}
